import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../../ui/PatientUIGovernor';
import { patientApi, type LabTestComparison } from '../../api';

/**
 * The same test, priced by every lab that publishes it.
 *
 * A lab store showed one lab's price with nothing to compare it against, so
 * the only way to find out that the test next door costs half as much was to
 * open every store in turn. `GET /lab/tests/compare` has been able to answer
 * that all along and nothing called it: it makes one pass over the published
 * catalogues, matches the names, and returns each lab's own price.
 *
 * Nothing here is modelled or scored. The row marked cheapest is simply the
 * lowest published price, and the page says exactly that. Where no second lab
 * publishes the test, `/lab/tests/comparable` says which tests *are* worth
 * comparing rather than leaving a dead end.
 */

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 14, boxShadow: T.shadow, fontFamily: T.font,
};

function tat(hours: number | null): string {
  if (typeof hours !== 'number' || hours <= 0) return '';
  if (hours < 24) return `Report in ${hours} h`;
  const days = Math.round(hours / 24);
  return `Report in ${days} day${days === 1 ? '' : 's'}`;
}

export default function LabCompare() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const testName = params.get('test') || '';

  const [data, setData] = useState<LabTestComparison | null>(null);
  const [others, setOthers] = useState<{ name: string; labCount: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sort, setSort] = useState<'price' | 'speed'>('price');

  const load = useCallback(async () => {
    if (!testName) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const r = await patientApi.lab.compareTest(testName);
      setData(r);
      // Only worth asking when this one has nothing to compare against.
      if ((r?.rows?.length ?? 0) < 2) {
        try {
          setOthers((await patientApi.lab.comparableTests() ?? []).slice(0, 8));
        } catch {
          setOthers([]);
        }
      } else {
        setOthers([]);
      }
    } catch (e: any) {
      setError(e?.body?.message || 'The labs could not be reached — check your connection.');
    } finally {
      setLoading(false);
    }
  }, [testName]);

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => {
    const list = [...(data?.rows ?? [])];
    if (sort === 'speed') {
      // A lab that publishes no turnaround has promised nothing, so it sorts
      // last rather than being treated as the fastest.
      list.sort((a, b) => (a.turnaroundHours ?? Infinity) - (b.turnaroundHours ?? Infinity));
    } else {
      list.sort((a, b) => a.price - b.price);
    }
    return list;
  }, [data, sort]);

  const cheapest = useMemo(
    () => (data?.rows ?? []).reduce<typeof rows[number] | null>(
      (lo, r) => (r.price > 0 && (!lo || r.price < lo.price) ? r : lo), null),
    [data],
  );

  if (!testName) {
    return (
      <div style={govPageContainer()}>
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>No test to compare</div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6 }}>
            Open a lab, find the test, and choose “Compare price”.
          </div>
          <button
            onClick={() => navigate('/patient/care-services/labs')}
            style={{
              marginTop: 12, padding: '9px 16px', borderRadius: 10, border: 'none',
              cursor: 'pointer', fontSize: 11.5, fontWeight: 700, color: T.white,
              background: T.teal, fontFamily: T.font,
            }}
          >
            Browse labs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 11.5, fontWeight: 700, color: T.teal, fontFamily: T.font, marginBottom: 8,
          }}
        >
          ← Back
        </button>
        <h1 style={{ ...GOV_TYPE.pageTitle }}>{data?.name || testName}</h1>
        <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
          What each lab charges for this test, from its own published catalogue.
        </p>
      </div>

      {error && <div style={{ ...card, borderColor: `${T.danger}55`, padding: 12, fontSize: 12, color: T.danger }}>{error}</div>}

      {!loading && rows.length > 1 && (
        <div style={{ display: 'flex', gap: 6 }}>
          {([['price', 'Cheapest first'], ['speed', 'Fastest report first']] as const).map(([v, label]) => {
            const on = sort === v;
            return (
              <button
                key={v}
                onClick={() => setSort(v)}
                style={{
                  padding: '7px 13px', borderRadius: 8, cursor: 'pointer',
                  fontSize: 11, fontWeight: 700, fontFamily: T.font,
                  color: on ? T.text : T.textTer,
                  background: on ? T.ice : 'transparent',
                  border: `1px solid ${on ? T.border : 'transparent'}`,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Reading the labs’ price lists…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🧪</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>No lab publishes this test</div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>
            “{testName}” is not in any lab’s published catalogue, so there is no price to compare.
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: T.textTer, fontFamily: T.font, lineHeight: 1.5 }}>
            {rows.length === 1
              ? 'Only one lab publishes this test, so there is nothing to compare it against yet.'
              : `${rows.length} labs publish this test.`}
            {cheapest && rows.length > 1 && ` The cheapest is ₹${cheapest.price.toLocaleString('en-IN')} at ${cheapest.labName} — that is the lowest published price, nothing more.`}
          </div>

          {rows.map((r) => {
            const best = cheapest?.labId === r.labId && rows.length > 1;
            return (
              <div key={r.labId} style={{ ...card, borderColor: best ? `${T.teal}66` : T.border }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>
                      {r.labName}
                      {r.verified && (
                        <span style={{
                          marginLeft: 6, fontSize: 9, fontWeight: 700, color: T.white,
                          background: T.teal, borderRadius: 6, padding: '2px 6px', verticalAlign: 'middle',
                        }}>
                          Verified
                        </span>
                      )}
                    </div>
                    {r.city && <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 3 }}>{r.city}</div>}
                    <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 5 }}>
                      {[
                        r.sampleType,
                        tat(r.turnaroundHours),
                        // The number of things actually measured — the same
                        // test name can cover a very different panel.
                        r.parameterCount > 0 ? `${r.parameterCount} parameter${r.parameterCount === 1 ? '' : 's'}` : null,
                      ].filter(Boolean).join(' · ') || 'No sample or turnaround published'}
                    </div>
                    {r.parameters.length > 0 && (
                      <div style={{ fontSize: 10, color: T.textTer, marginTop: 4, lineHeight: 1.5 }}>
                        {r.parameters.slice(0, 6).join(', ')}{r.parameters.length > 6 ? ` +${r.parameters.length - 6} more` : ''}
                      </div>
                    )}
                    {best && (
                      <div style={{ fontSize: 10.5, color: T.teal, fontWeight: 700, marginTop: 6 }}>
                        Lowest published price
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: T.navy }}>
                      {r.price > 0 ? `₹${r.price.toLocaleString('en-IN')}` : 'Price on request'}
                    </div>
                    <button
                      onClick={() => navigate(`/patient/care-services/labs/${r.labSlug || r.labId}`)}
                      style={{
                        marginTop: 8, padding: '8px 14px', borderRadius: 10, border: 'none',
                        cursor: 'pointer', fontSize: 11, fontWeight: 700, color: T.white,
                        background: T.teal, fontFamily: T.font, whiteSpace: 'nowrap',
                      }}
                    >
                      Open lab
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {!loading && others.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>Tests you can compare</div>
          <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 3 }}>
            These are published by more than one lab.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {others.map((o) => (
              <button
                key={o.name}
                onClick={() => setParams({ test: o.name }, { replace: true })}
                style={{
                  padding: '7px 12px', borderRadius: 18, cursor: 'pointer',
                  fontSize: 11, fontWeight: 700, fontFamily: T.font, color: T.textSec,
                  background: T.white, border: `1px solid ${T.border}`,
                }}
              >
                {o.name} · {o.labCount} labs
              </button>
            ))}
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div style={{ fontSize: 10.5, color: T.textTer, fontFamily: T.font, lineHeight: 1.5 }}>
          Prices are what each lab published and can change; home collection, if the lab offers it, may be charged
          separately at booking.
        </div>
      )}
    </div>
  );
}
